/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: AI Voice Cloning features section showcasing key features with alternating layout
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface FeatureItemProps {
  title: string;
  description: string;
  ctaText: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  ctaText,
  imageUrl,
  imageAlt,
  reverse = false,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton text={ctaText} />
      </div>
    </div>
  );
};

const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      title: 'Choose from 100+ Realistic AI Presenter',
      description:
        'FalcoCut provides a wide selection of over 100 realistic AI presenters who look, speak, and act like real people. From professional business spokespeople and educators to casual influencers and friendly customer service reps, you can instantly select a presenter that fits your content. Each AI presenter delivers scripts with natural expressions, gestures, and tone, helping you produce engaging videos for product demos, training courses, or social media campaigns—without the need to hire on-camera talent.',
      ctaText: 'Turn Text to Speech Now',
      imageAlt: 'Over 100 realistic AI presenters',
      imageUrl:
        'https://assets.falcocut.ai/ai-voice-cloning/feature-realistic-presenter.webp',
    },
    {
      title: 'Human-Like Voice Replicas',
      description:
        'Forget robotic or flat-sounding audio. Our deep learning algorithms analyze speech patterns, intonation, and emotional detail to generate audio that truly sounds human. From subtle pauses to expressive tones, your cloned voice captures the personality and style of the original speaker. Perfect for podcasts, audiobooks, and brand storytelling where authenticity matters most.',
      ctaText: 'Clone Your Voice',
      imageUrl:
        'https://assets.falcocut.ai/ai-voice-cloning/feature-human-like-voice.webp',
      imageAlt: 'Human-like voice replicas',
    },

    {
      title: 'Multi-Language & Accent Support',
      description:
        'Reach a global audience without language barriers. Our AI voice cloning technology supports multiple languages, including English, Mandarin, Japanese, Korean, Spanish, and more. Each cloned voice preserves its natural tone while adapting to different linguistic rules and accents, allowing you to scale your content internationally with consistent quality.',
      ctaText: 'Clone Your Voice',
      imageUrl: 'https://assets.falcocut.ai/ai-voice-cloning/feature-multilingual.webp',
      imageAlt: 'Multi-language and accent support',
    },
    {
      title: 'Customizable Style & Emotion',
      description:
        "Control not just the words, but the way they're delivered. Adjust pitch, pacing, and emotional tone to create voices that fit any scenario. Make your AI clone sound energetic for social media ads, calm for meditation guides, or professional for corporate presentations. This flexibility ensures every project has the right voice for the right audience.",
      ctaText: 'Clone Your Voice',
      imageUrl:
        'https://assets.falcocut.ai/ai-voice-cloning/feature-customizable-style.webp',
      imageAlt: 'Customizable voice style and emotion',
    },

    {
      title: 'Text-to-Speech & Speech-to-Speech',
      description:
        'Transform both written text and spoken audio with your cloned voice. Input a script and hear it read back in your personalized voice clone, or convert existing recordings into a completely different voice while maintaining timing and emotion. This dual capability is ideal for content creators who want efficiency, musicians experimenting with vocals, or businesses generating dynamic audio experiences.',
      ctaText: 'Clone Your Voice',
      imageUrl: 'https://assets.falcocut.ai/ai-voice-cloning/feature-text-to-speech.webp',
      imageAlt: 'Text-to-speech and speech-to-speech',
    },
    {
      title: 'Scalable for Any Project',
      description:
        'From fun personal projects to enterprise-level media production, our AI voice cloning adapts to your needs. Use it to create educational e-learning modules, automated customer service lines, promotional videos, or even personalized birthday greetings. Whatever your purpose, you save time, cut costs, and still deliver consistent, professional-quality audio.',
      ctaText: 'Clone Your Voice',
      imageUrl: 'https://assets.falcocut.ai/ai-voice-cloning/feature-scalable.webp',
      imageAlt: 'Scalable for any project',
    },
    {
      title: 'Privacy & Security Built-In',
      description:
        "We take data protection seriously. All voice samples and cloned outputs are processed securely, and you can request deletion at any time. Our platform adheres to strict privacy standards, so you can use AI voice cloning with full confidence that your data—and your clients' data—is safe.",
      ctaText: 'Clone Your Voice',
      imageUrl:
        'https://assets.falcocut.ai/ai-voice-cloning/feature-privacy-security.webp',
      imageAlt: 'Privacy and security built-in',
    },
    {
      title: 'Integrated Voice Cloning, Avatars & Translation',
      description:
        'FalcoCut combines voice cloning with AI avatars and video translation in one platform. Clone your voice, pair it with a realistic avatar, and translate your video into different languages—all in one streamlined workflow. Perfect for creators and businesses who want to produce professional, multilingual video content without juggling multiple tools.',
      ctaText: 'Clone Your Voice',
      imageUrl: 'https://assets.falcocut.ai/ai-voice-cloning/feature-integrated.webp',
      imageAlt: 'Integrated voice cloning, avatars and translation',
    },
  ];

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Powerful Features That Redefine AI Voice Cloning
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          Explore a complete suite of AI-powered features — from instant voice cloning and
          multilingual support to avatars and translations. Whether you&apos;re producing
          ads, podcasts, e-learning, or global campaigns, our technology delivers
          lifelike, secure, and customizable voice experiences for every project.
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              ctaText={feature.ctaText}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PowerfulFeaturesSection;
